import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { useToast } from '../../../hooks/use-toast';
import { Settings, Clock, Gift, MessageSquare, Phone, Calendar, Play, History, AlertTriangle, Send, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import DeliverabilityDashboard from './DeliverabilityDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AutomationAdmin = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    day_of_week: 'tue',
    hour: 10,
    minute: 0,
    prize: 'Free Brownie Sundae',
    winner_sms_template: '',
    announcement_sms_template: '',
    admin_phone: '',
    low_balance_threshold: 10.00,
    batch_size: 25,
    batch_delay_seconds: 300,
    message_delay_seconds: 2
  });
  const [history, setHistory] = useState([]);
  const [nextRun, setNextRun] = useState(null);
  const [smsBalance, setSmsBalance] = useState(null);

  // Test SMS state
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // AI variation preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);

  const dayOptions = [
    { value: 'mon', label: 'Monday' },
    { value: 'tue', label: 'Tuesday' },
    { value: 'wed', label: 'Wednesday' },
    { value: 'thu', label: 'Thursday' },
    { value: 'fri', label: 'Friday' },
    { value: 'sat', label: 'Saturday' },
    { value: 'sun', label: 'Sunday' }
  ];

  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`
  }));

  useEffect(() => {
    fetchSettings();
    fetchHistory();
    fetchNextRun();
    fetchSmsBalance();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/automation/weekly-drawing/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/automation/weekly-drawing/history?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchNextRun = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/automation/weekly-drawing/next-run`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNextRun(data);
      }
    } catch (error) {
      console.error('Error fetching next run:', error);
    }
  };

  const fetchSmsBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/automation/weekly-drawing/sms-balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSmsBalance(data);
      }
    } catch (error) {
      console.error('Error fetching SMS balance:', error);
    }
  };

  const saveSettings = async () => {
    // Hard-block save if either template exceeds 160 chars
    const winnerLen = (settings.winner_sms_template || '').length;
    const announceLen = (settings.announcement_sms_template || '').length;
    if (winnerLen > 160 || announceLen > 160) {
      toast({
        title: 'Templates exceed 160 chars',
        description: `Winner: ${winnerLen}/160 · Announcement: ${announceLen}/160. Trim them before saving.`,
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/automation/weekly-drawing/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({ title: '✅ Settings Saved', description: data.message });
        fetchNextRun();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm('Are you sure you want to run the weekly drawing NOW? This will pick a winner and send SMS messages.')) {
      return;
    }
    
    setRunningNow(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/automation/weekly-drawing/run-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Drawing Complete!',
          description: `Winner: ${data.winner_name}. ${data.sms_sent} SMS sent.`
        });
        fetchHistory();
        fetchSmsBalance();
      } else {
        toast({ title: 'Drawing Failed', description: data.detail || 'Unknown error', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to run drawing', variant: 'destructive' });
    } finally {
      setRunningNow(false);
    }
  };

  const sendTestSMS = async () => {
    if (!testPhone.trim()) {
      toast({ title: 'Phone required', description: 'Enter a phone number to send the test to.', variant: 'destructive' });
      return;
    }
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_URL}/api/automation/sms/test-delivery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          phone: testPhone.trim(),
          message: testMessage.trim() || null,
        }),
      });
      const data = await res.json();
      setTestResult(data);
      if (data.success) {
        toast({ title: 'Test SMS sent', description: `Telnyx accepted message ${data.message_id || ''}. Check the phone in 30-60 sec.` });
      } else {
        toast({ title: 'Test SMS failed', description: data.error || 'Telnyx rejected the send.', variant: 'destructive' });
      }
    } catch (e) {
      setTestResult({ success: false, error: e?.message || 'Network error' });
      toast({ title: 'Network error', description: e?.message || 'Failed to call test endpoint', variant: 'destructive' });
    } finally {
      setSendingTest(false);
    }
  };

  const previewVariations = async () => {
    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const res = await fetch(`${API_URL}/api/automation/sms/preview-variations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ template: settings.announcement_sms_template, count: 3 }),
      });
      const data = await res.json();
      setPreviewResult(data);
    } catch (e) {
      setPreviewResult({ success: false, error: e?.message || 'Network error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Weekly Drawing Automation</h2>
          <p className="text-gray-500">Automatically pick winners and send SMS notifications</p>
        </div>
        <div className="flex items-center gap-4">
          {nextRun?.enabled && (
            <div className="text-sm text-gray-600 bg-gray-100 px-4 py-2 rounded-lg">
              <Clock className="w-4 h-4 inline mr-1" />
              Next run: {nextRun.day_name} {nextRun.time}
            </div>
          )}
        </div>
      </div>

      {/* Deliverability Dashboard — pre-blast health check */}
      <DeliverabilityDashboard />

      {/* Enable/Disable Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Automation Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <Label className="text-base font-medium">Enable Weekly Drawing Automation</Label>
              <p className="text-sm text-gray-500">When enabled, the system will automatically pick winners and send SMS</p>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                data-testid="automation-enabled-toggle"
              />
              <span className={`text-sm font-semibold ${settings.enabled ? 'text-green-600' : 'text-gray-400'}`}>
                {settings.enabled ? 'ON' : 'OFF'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SMS Balance Warning */}
      {smsBalance && (
        <Card className={smsBalance.is_low ? 'border-red-300 bg-red-50' : smsBalance.balance < 20 ? 'border-yellow-300 bg-yellow-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {smsBalance.is_low ? (
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                ) : smsBalance.balance < 20 ? (
                  <AlertTriangle className="w-6 h-6 text-yellow-500" />
                ) : (
                  <MessageSquare className="w-5 h-5 text-green-500" />
                )}
                <div>
                  <p className="font-semibold text-gray-900">
                    SMS Credit Balance: <span className={smsBalance.is_low ? 'text-red-600' : smsBalance.balance < 20 ? 'text-yellow-600' : 'text-green-600'}>${smsBalance.balance.toFixed(2)}</span>
                  </p>
                  <p className="text-sm text-gray-500">{smsBalance.messages_sent} messages sent total</p>
                </div>
              </div>
              {smsBalance.is_low && (
                <span className="text-xs font-bold text-red-600 bg-red-100 px-3 py-1 rounded-full">LOW BALANCE</span>
              )}
              {!smsBalance.can_send && (
                <span className="text-xs font-bold text-red-600 bg-red-100 px-3 py-1 rounded-full">CANNOT SEND - Below $5</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Schedule
          </CardTitle>
          <CardDescription>When should the drawing run?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Day of Week</Label>
              <select
                value={settings.day_of_week}
                onChange={(e) => setSettings({ ...settings, day_of_week: e.target.value })}
                className="w-full mt-1 p-2 border rounded-lg"
              >
                {dayOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Time (CST)</Label>
              <select
                value={settings.hour}
                onChange={(e) => setSettings({ ...settings, hour: parseInt(e.target.value) })}
                className="w-full mt-1 p-2 border rounded-lg"
              >
                {hourOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Central Standard Time (Texas)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prize Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Prize Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Prize Name</Label>
            <Input
              value={settings.prize}
              onChange={(e) => setSettings({ ...settings, prize: e.target.value })}
              placeholder="Free Brownie Sundae"
              className="mt-1"
              data-testid="prize-name-input"
            />
            <p className="text-xs text-gray-500 mt-1">This will be included in the winner SMS</p>
          </div>
        </CardContent>
      </Card>

      {/* SMS Templates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            SMS Templates
          </CardTitle>
          <CardDescription>
            Available variables: {'{{name}}'}, {'{{prize}}'}, {'{{code}}'}. 
            <span className="text-orange-600 font-medium"> Avoid ALL CAPS words, URLs, and emojis to prevent carrier spam filtering.</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <p className="font-semibold text-amber-800 mb-1">Carrier Spam Prevention Tips:</p>
            <ul className="text-amber-700 space-y-1 list-disc list-inside">
              <li>Start with your business name: "1st Ave ICP:"</li>
              <li>Avoid words in ALL CAPS (e.g. FREE, WON, WINNER)</li>
              <li>Do not include URLs/links in SMS messages</li>
              <li>Always include "Reply STOP to opt out" at the end</li>
              <li>Keep messages under 160 characters when possible (1 segment)</li>
            </ul>
          </div>
          <div>
            <Label>Winner SMS Template</Label>
            <Textarea
              value={settings.winner_sms_template}
              onChange={(e) => setSettings({ ...settings, winner_sms_template: e.target.value })}
              rows={4}
              maxLength={160}
              className={`mt-1 font-mono text-sm ${(settings.winner_sms_template?.length || 0) > 160 ? 'border-red-400 focus:border-red-500' : ''}`}
              data-testid="winner-sms-template"
            />
            <p className={`text-xs mt-1 ${(settings.winner_sms_template?.length || 0) > 160 ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
              Sent only to the winner • <span data-testid="winner-char-count">{settings.winner_sms_template?.length || 0}</span> / 160 chars
              {(settings.winner_sms_template?.length || 0) > 160 ? ' — EXCEEDS LIMIT, will be truncated by carrier!' : ' (1 segment)'}
            </p>
          </div>
          <div>
            <Label>Announcement SMS Template</Label>
            <Textarea
              value={settings.announcement_sms_template}
              onChange={(e) => setSettings({ ...settings, announcement_sms_template: e.target.value })}
              rows={4}
              maxLength={160}
              className={`mt-1 font-mono text-sm ${(settings.announcement_sms_template?.length || 0) > 160 ? 'border-red-400 focus:border-red-500' : ''}`}
              data-testid="announcement-sms-template"
            />
            <p className={`text-xs mt-1 ${(settings.announcement_sms_template?.length || 0) > 160 ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
              Sent to all other subscribers • <span data-testid="announcement-char-count">{settings.announcement_sms_template?.length || 0}</span> / 160 chars
              {(settings.announcement_sms_template?.length || 0) > 160 ? ' — EXCEEDS LIMIT, will be truncated by carrier!' : ' (1 segment)'}
            </p>
            {!(settings.announcement_sms_template || '').includes('{{name}}') && (
              <div
                className="mt-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-md px-3 py-2 text-xs flex items-start gap-2"
                data-testid="missing-name-warning"
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Missing personalization.</strong> Add <code className="bg-amber-100 px-1 rounded font-bold">{'{{name}}'}</code> to your template so each contact gets a personalized SMS. Carriers fingerprint identical bulk messages — personalization plus AI variation makes each send unique.
                </span>
              </div>
            )}
          </div>

          {/* AI Message Variation — defeats carrier content fingerprinting */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3" data-testid="ai-variation-card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Label className="text-base font-bold text-purple-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  AI Message Variation (per batch)
                </Label>
                <p className="text-xs text-purple-700 mt-1">
                  Carriers flag identical bulk SMS. When enabled, the AI re-words the announcement template
                  for each batch of {settings.batch_size || 25} so every batch sends a different (but
                  same-meaning) variant. Won&apos;t bypass an active block, but prevents future ones.
                </p>
              </div>
              <Switch
                checked={settings.auto_vary_messages !== false}
                onCheckedChange={(c) => setSettings({ ...settings, auto_vary_messages: c })}
                data-testid="auto-vary-toggle"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={previewVariations}
              disabled={previewLoading || (settings.announcement_sms_template?.length || 0) > 160}
              className="border-purple-400 text-purple-700 hover:bg-purple-100"
              data-testid="preview-variations-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {previewLoading ? 'Generating…' : 'Preview 3 AI variations'}
            </Button>
            {previewResult && (
              <div className="space-y-2 pt-2" data-testid="preview-variations-result">
                {previewResult.success === false ? (
                  <p className="text-sm text-red-600">{previewResult.error}</p>
                ) : (
                  <>
                    <p className="text-xs text-purple-700 font-bold uppercase tracking-wide">
                      Sample variations the AI would use:
                    </p>
                    {previewResult.variations?.map((v, i) => (
                      <div
                        key={i}
                        className={`text-xs bg-white border rounded p-2 ${
                          v.is_same_as_original ? 'border-amber-300' : 'border-purple-200'
                        }`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <span className="font-mono text-gray-700 flex-1">{v.text}</span>
                          <span className={`whitespace-nowrap text-xs ${v.length > 160 ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                            {v.length}/160
                          </span>
                        </div>
                        {v.is_same_as_original && (
                          <p className="text-amber-700 mt-1 italic">⚠ LLM returned original (couldn&apos;t safely vary)</p>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm('This will replace your current SMS templates with carrier-friendly versions optimized to avoid spam filters. Continue?')) return;
              try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/api/automation/weekly-drawing/update-templates-carrier-friendly`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                  toast({ title: 'Templates Updated', description: 'SMS templates have been updated to carrier-friendly versions.' });
                  fetchSettings();
                } else {
                  toast({ title: 'Error', description: 'Failed to update templates', variant: 'destructive' });
                }
              } catch (err) {
                toast({ title: 'Error', description: 'Failed to update templates', variant: 'destructive' });
              }
            }}
            className="border-orange-400 text-orange-600 hover:bg-orange-50"
            data-testid="update-templates-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Reset to Carrier-Friendly Templates
          </Button>
        </CardContent>
      </Card>

      {/* Batch Sending Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Batch Sending (Spam Prevention)
          </CardTitle>
          <CardDescription>Control how SMS messages are sent to avoid carrier spam filters. Messages are sent in small batches with pauses between them.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Batch Size</Label>
              <Input
                type="number"
                min={5}
                max={50}
                value={settings.batch_size}
                onChange={(e) => setSettings({ ...settings, batch_size: parseInt(e.target.value) || 25 })}
                className="mt-1"
                data-testid="batch-size-input"
              />
              <p className="text-xs text-gray-500 mt-1">Messages per batch (recommended: 25)</p>
            </div>
            <div>
              <Label>Batch Delay (seconds)</Label>
              <Input
                type="number"
                min={60}
                max={900}
                value={settings.batch_delay_seconds}
                onChange={(e) => setSettings({ ...settings, batch_delay_seconds: parseInt(e.target.value) || 300 })}
                className="mt-1"
                data-testid="batch-delay-input"
              />
              <p className="text-xs text-gray-500 mt-1">Pause between batches (recommended: 300 = 5 min)</p>
            </div>
            <div>
              <Label>Message Delay (seconds)</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={settings.message_delay_seconds}
                onChange={(e) => setSettings({ ...settings, message_delay_seconds: parseInt(e.target.value) || 2 })}
                className="mt-1"
                data-testid="message-delay-input"
              />
              <p className="text-xs text-gray-500 mt-1">Delay between each SMS in a batch</p>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <p className="font-medium">Estimated send time for 183 contacts:</p>
            <p>
              {Math.ceil(183 / (settings.batch_size || 25))} batches × {settings.batch_size || 25} messages.
              Total: ~{Math.round(((183 * (settings.message_delay_seconds || 2)) + (Math.max(0, Math.ceil(183 / (settings.batch_size || 25)) - 1) * (settings.batch_delay_seconds || 300))) / 60)} minutes
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Send Test SMS — for verifying deliverability (esp. after Telnyx appeal clears) */}
      <Card data-testid="send-test-sms-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Send Test SMS
          </CardTitle>
          <CardDescription>
            Fire a single test SMS to verify deliverability. Bypasses batching/throttling.
            Use this after the Telnyx carrier appeal clears to confirm the block is lifted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Phone Number</Label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="8175551234 or +18175551234"
              className="mt-1"
              data-testid="test-sms-phone-input"
            />
            <p className="text-xs text-gray-500 mt-1">10-digit US number — your own phone is recommended for the first test.</p>
          </div>
          <div>
            <Label>Message (optional)</Label>
            <Textarea
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Leave blank to send the default: '[TEST] 1st Ave ICP deliverability test. Reply STOP to unsubscribe.'"
              rows={3}
              className="mt-1"
              data-testid="test-sms-message-input"
            />
          </div>
          <Button
            onClick={sendTestSMS}
            disabled={sendingTest || !testPhone.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            data-testid="send-test-sms-btn"
          >
            <Send className="w-4 h-4" />
            {sendingTest ? 'Sending…' : 'Send Test SMS'}
          </Button>

          {testResult && (
            <div
              className={`rounded-lg border-2 p-4 ${
                testResult.success
                  ? 'bg-green-50 border-green-300'
                  : 'bg-red-50 border-red-300'
              }`}
              data-testid="test-sms-result"
            >
              <div className="flex items-start gap-3">
                {testResult.success
                  ? <CheckCircle2 className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" />
                  : <XCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />}
                <div className="flex-1 text-sm">
                  <p className={`font-bold ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.success ? 'Telnyx accepted the message' : 'Send failed'}
                  </p>
                  {testResult.message_id && (
                    <p className="text-gray-700 mt-1"><span className="font-mono text-xs">Message ID:</span> <span className="font-mono text-xs">{testResult.message_id}</span></p>
                  )}
                  {testResult.telnyx_status && (
                    <p className="text-gray-700"><span className="font-medium">Status:</span> {testResult.telnyx_status}</p>
                  )}
                  {testResult.error && (
                    <p className="text-red-700 mt-1"><span className="font-medium">Error:</span> {testResult.error}</p>
                  )}
                  {testResult.note && (
                    <p className="text-gray-600 italic mt-2 text-xs">{testResult.note}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Admin Alerts
          </CardTitle>
          <CardDescription>Get notified when SMS credits are low</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Admin Phone Number</Label>
            <Input
              value={settings.admin_phone}
              onChange={(e) => setSettings({ ...settings, admin_phone: e.target.value })}
              placeholder="+1234567890"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Receives SMS alerts when balance is low</p>
          </div>
          <div>
            <Label>Low Balance Threshold ($)</Label>
            <Input
              type="number"
              min={1}
              value={settings.low_balance_threshold}
              onChange={(e) => setSettings({ ...settings, low_balance_threshold: parseFloat(e.target.value) || 10 })}
              className="mt-1 w-32"
            />
            <p className="text-xs text-gray-500 mt-1">Alert when balance falls below this amount</p>
          </div>
        </CardContent>
      </Card>

      {/* Save & Run Buttons */}
      <div className="flex gap-4">
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
          data-testid="save-automation-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        <Button 
          onClick={runNow} 
          disabled={runningNow || !settings.enabled}
          variant="outline"
          className="border-green-500 text-green-600 hover:bg-green-50"
          data-testid="run-drawing-now-btn"
        >
          <Play className="w-4 h-4 mr-2" />
          {runningNow ? 'Running...' : 'Run Drawing Now'}
        </Button>
      </div>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Drawing History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No drawings yet</p>
          ) : (
            <div className="space-y-3">
              {history.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{item.winner_name}</p>
                    <p className="text-sm text-gray-500">
                      Prize: {item.prize} • {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-green-600">{item.sms_sent} SMS sent</p>
                    {item.sms_failed > 0 && (
                      <p className="text-red-500">{item.sms_failed} failed</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AutomationAdmin;
